import { Component, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { FirebaseX } from '@awesome-cordova-plugins/firebase-x/ngx';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { ModalController, Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { Nullable } from 'src/core/common/domain/types/types';
import { EventsService } from 'src/core/events/events.service';
import { SessionService } from 'src/core/session/domain/session.service';
import { Storage } from 'src/core/storage/domain/storage';
import { MasterDataService } from 'src/core/master-data/domain/master-data-service';
import { FiltersService } from 'src/core/filters/domain/filters-service';
import { Logout } from 'src/core/authentication/application/logout/logout';
import { PlatformService } from 'src/core/common/domain/platform/platform.service';
import { AppService } from './app.service';
import { register } from 'swiper/element/bundle';
import { ApiService } from 'src/core/api/domain/api.service';
import { MessageHelper } from './helpers/message-helper';
import { SystemService } from 'src/core/system/system.service';
import { TaskService } from 'src/core/tasks/domain/task.service';
import { NotificationsService } from 'src/core/notifications/domain/notifications.service';
import { SyncNotifications } from 'src/core/notifications/application/sync/sync-notifications';
import { DataService } from 'src/core/data/domain/data-service';
import { NFCResponse, NFCService } from 'src/core/nfc/domain/nfc.service';
import { NFCMessageParser } from 'src/core/nfc/domain/nfc-message-parser';
import { CredentialsScannedEvent } from 'src/core/events/credentials-scanned.event';
import { UserLoggedOutEvent } from 'src/core/events/user-logged-out.event';
import { QueueService } from 'src/core/queue/domain/queue.service';
import { ConnectivityService } from 'src/core/connection/domain/connectivity.service';
import { MandatoryUpdateModalPage } from 'src/app/components/modals/mandatory-update-modal/mandatory-update-modal';
import { TranslatorService } from 'src/core/translator/domain/translator-service';
import { LocationScannedEvent } from 'src/core/events/location-scanned.event';
import { addIcons } from 'ionicons';
import * as moment from 'moment-timezone';
import { GarbageCollectorService } from 'src/core/garbage-collector/domain/garbage-collector.service';
import { UserPresenceService } from 'src/core/presence/domain/user-presence.service';
import { DeviceService } from 'src/core/device/device.service';
import { PushNotificationService } from 'src/core/push/domain/push-notification.service';

// we need to call Swiper's register function to globally register Swiper's custom elements.
register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  private handlingSessionExpiration = false;
  private appIsOutdated$: Nullable<Subscription> = null;
  private subscriptionUserLoggedIn$: Nullable<Subscription> = null;
  private subscriptionUserLoggedOut$: Nullable<Subscription> = null;
  private subscriptionAuthorizationExpired$: Nullable<Subscription> = null;
  private nfcSubscription: Subscription;
  private subscriptionCredentialsScanned$: Subscription;
  private newAppVersionAvailable$: Subscription;

  constructor(
    private readonly apiService: ApiService,
    private readonly appService: AppService,
    private readonly connectivityService: ConnectivityService,
    private readonly dataService: DataService,
    private readonly deviceService: DeviceService,
    private readonly events: EventsService,
    private readonly modalCtrl: ModalController,
    private readonly filtersService: FiltersService,
    private readonly firebaseX: FirebaseX,
    private readonly garbageCollectorService: GarbageCollectorService,
    private readonly logout: Logout,
    private readonly masterDataService: MasterDataService,
    private readonly messageHelper: MessageHelper,
    private readonly nfcService: NFCService,
    private readonly ngZone: NgZone,
    private readonly notificationsService: NotificationsService,
    private readonly platform: Platform,
    private readonly platformService: PlatformService,
    private readonly pushNotificationService: PushNotificationService,
    private readonly queueService: QueueService,
    private readonly router: Router,
    private readonly session: SessionService,
    private readonly statusBar: StatusBar,
    private readonly storage: Storage,
    private readonly systemService: SystemService,
    private readonly translation: TranslatorService,
    private readonly taskService: TaskService,
    private readonly userPresenceService: UserPresenceService,
    private readonly syncNotifications: SyncNotifications
  ) {
    // fire-and-forget, y el fallo se descarta a propósito: si la rejection subiera,
    // CustomErrorHandler enseñaría un toast de error sin contexto para el usuario.
    // Se deja en consola para no perderlo del todo al depurar.
    this.initializeApp().catch(console.error);

    // Register our custom icons to avoid Chrome console error when developing
    addIcons({
      'rs-icon-input': 'assets/images/icon-input.svg',
      'rs-iss-attach': 'assets/images/iss-attach.svg',
      'rs-iss-calendar': 'assets/images/iss-calendar.svg',
      'rs-iss-centre': 'assets/images/iss-centre.svg',
      'rs-iss-clock': 'assets/images/iss-clock.svg',
      'rs-iss-close': 'assets/images/iss-close.svg',
      'rs-iss-check-circled': 'assets/images/icon-check-circled.svg',
      'rs-iss-cog': 'assets/images/icon-tools.svg',
      'rs-iss-contact': 'assets/images/iss-contact.svg',
      'rs-iss-cycle': 'assets/images/iss-cycle.svg',
      'rs-iss-download': 'assets/images/iss-download.svg',
      'rs-iss-edit': 'assets/images/iss-edit.svg',
      'rs-iss-ellipsis-vertical': 'assets/images/iss-ellipsis-vertical.svg',
      'rs-iss-exit': 'assets/images/iss-exit.svg',
      'rs-iss-file': 'assets/images/iss-file.svg',
      'rs-iss-info': 'assets/images/iss-info.svg',
      'rs-iss-mail': 'assets/images/icon-mail.svg',
      'rs-iss-nfc-read': 'assets/images/icon-nfc-read.svg',
      'rs-iss-nfc-write': 'assets/images/icon-nfc-record.svg',
      'rs-iss-notification': 'assets/images/iss-notification.svg',
      'rs-iss-notification-read-all': 'assets/images/iss-envelop-btn_read.svg',
      'rs-iss-notification-read': 'assets/images/iss-envelop_read.svg',
      'rs-iss-notification-unread': 'assets/images/iss-envelop_unread.svg',
      'rs-iss-notifications': 'assets/images/iss-bell.svg',
      'rs-iss-notifications-unread': 'assets/images/iss-bell_unread.svg',
      'rs-iss-person': 'assets/images/iss-person.svg',
      'rs-iss-scan-nfc': 'assets/images/scan-nfc.svg',
      'rs-iss-shield': 'assets/images/iss-shield.svg',
      'rs-iss-sort': 'assets/images/iss-sort.svg',
      'rs-iss-timezone-clock': 'assets/images/iss-timezone-clock.svg',
      'rs-iss-user': 'assets/images/icon-user.svg',
      'rs-iss-warning': 'assets/images/iss-warning.svg',
    });
  }

  private async initializeApp() {
    await this.platform.ready();
    this.addBackHardwareButtonToExitApp();
    this.printCurrentPlatformInfo();
    this.addEventListeners();
    await this.configureCordova();
    await this.storage.ready();
    await this.resetAllStorageIfNeeded();

    // all services that implements initializable interface should be initialized
    await this.deviceService.init();
    await this.apiService.init();
    await this.session.init();
    await this.masterDataService.init();
    await this.taskService.init();
    await this.filtersService.init();
    await this.queueService.init();
    await this.notificationsService.init();
    await this.translation.init();

    await this.systemService.storeCurrentVersionToStorage();
    await this.dataService.checkCurrentData();
  }

  private addBackHardwareButtonToExitApp() {
    this.platform.backButton.subscribeWithPriority(-1, () => {
      if (!this.platform.is('cordova')) {
        return;
      }

      const currentUrl = this.router.url;
      const rootPaths = ['/login', '/tabs/tasks', '/tabs/master-data', '/tabs/synchronization', '/tabs/attendance'];
      if (rootPaths.indexOf(currentUrl) === -1) {
        return;
      }

      // eslint-disable-next-line
      if (!navigator['app']) {
        return;
      }

      // eslint-disable-next-line
      navigator['app'].exitApp();
    });
  }

  private async resetAllStorageIfNeeded() {
    const needsToBeReset = await this.systemService.storageNeedsToBeReset();
    if (!needsToBeReset) {
      return;
    }

    await this.doLogout();
    await this.systemService.resetStorage();
    window.location.reload();
  }

  private printCurrentPlatformInfo(): void {
    // eslint-disable-next-line no-console
    console.info(`%c Platform: ${this.platform.platforms().join(', ')}`, 'background: #222; color: #bada55');
  }

  private addEventListeners(): void {
    this.subscribeToPauseResumeEvents();
    this.subscribeToAppIsOutdatedEvent();
    this.subscribeToNewAppVersionIsAvailableEvent();
    this.subscriptionUserLoggedIn$ = this.events.userLoggedIn$.subscribe(async () => {
      await this.userPresenceService.start();
      await this.pushNotificationService.register();
      await this.syncNotificationsIgnoringFailures();
    });
    this.subscriptionUserLoggedOut$ = this.events.userLoggedOut$.subscribe(async () => {
      this.userPresenceService.stop();
      await this.pushNotificationService.unregister();
      await this.doLogout();
    });
    this.subscriptionAuthorizationExpired$ = this.events.authorizationExpired$.subscribe(async () => {
      if (this.handlingSessionExpiration) {
        return;
      }
      this.handlingSessionExpiration = true;
      try {
        this.userPresenceService.stop();
        await this.dismissModalsAfterSessionExpiration();
        await this.messageHelper.showWarning(this.translation.translate('MESSAGES.SESSION_HAS_EXPIRED'));
        await this.doLogout();
      } finally {
        this.handlingSessionExpiration = false;
      }
    });
    // NFC read
    this.nfcService.ready$.subscribe((ready) => {
      if (ready) {
        this.unsubscribeFromNFC();
        this.nfcSubscription = this.nfcService.listen().subscribe({
          next: async (response) => await this.processNFCData(response),
          error: async (error) => await this.messageHelper.showError(JSON.stringify(error)),
        });
      } else {
        this.unsubscribeFromNFC();
      }
    });
    this.subscriptionCredentialsScanned$ = this.events.credentialsScanned$.subscribe(async (event) => {
      const isAuthenticated = await this.session.isAuthenticatedPromise();
      if (!isAuthenticated) {
        // alert('Scanned while NOT authenticated, skipping since login would take care of the event');
        return;
      }
      // alert('Scanned while authenticated, logout + login with: ' + event.username + '::' + event.password);
      if (await this.userSwitchIsBlockedBySynchronization()) {
        return;
      }

      if (this.queueService.getNumElements() > 0) {
        const ok = await this.messageHelper.askConfirmationAboutLosingPendingChanges();
        if (!ok) {
          return;
        }

        // a pass may have started while the confirmation was open
        if (await this.userSwitchIsBlockedBySynchronization()) {
          return;
        }
      }

      this.events.publishLoggedOutEvent(new UserLoggedOutEvent());
      setTimeout(() => {
        this.events.publishCredentialsScannedEvent(event);
      }, 500);
    });

    this.connectivityService.status$.subscribe(async (hasConnection) => {
      if (hasConnection) {
        await this.queueService.processQueue();
        await this.syncNotificationsIgnoringFailures();
      }
    });
  }

  private async userSwitchIsBlockedBySynchronization(): Promise<boolean> {
    if (!this.userPresenceService.isLogoutBlocked()) {
      return false;
    }

    await this.messageHelper.showInfo(this.translation.translate('PRESENCE.LOGOUT_BLOCKED_WHILE_SYNCHRONIZING'), 3000);
    return true;
  }

  private unsubscribeFromNFC() {
    if (this.nfcSubscription) {
      this.nfcSubscription.unsubscribe();
    }
  }

  private async processNFCData(response: NFCResponse) {
    if (NFCMessageParser.isCredentialsMessage(response.data)) {
      const credentialsMessage = NFCMessageParser.processCredentialsMessage(response.data, response.serialNumber);
      this.events.publishCredentialsScannedEvent(
        new CredentialsScannedEvent(
          credentialsMessage.username,
          credentialsMessage.password,
          credentialsMessage.serialNumber
        )
      );
      return;
    }

    // otherwise, location message
    const locationMessage = NFCMessageParser.processLocationMessage(response.data);
    this.events.publishLocationScannedEvent(new LocationScannedEvent(locationMessage.locationCode));
  }

  private subscribeToAppIsOutdatedEvent() {
    this.appIsOutdated$ = this.events.appIsOutdated$.subscribe(async () => {
      const modal = await this.modalCtrl.create({
        component: MandatoryUpdateModalPage,
        componentProps: {},
        backdropDismiss: false,
        cssClass: 'is-modal-on-top',
      });
      await modal.present();
    });
  }

  private subscribeToNewAppVersionIsAvailableEvent() {
    this.newAppVersionAvailable$ = this.events.newAppVersionAvailable$.subscribe(async () => {
      await this.displayNewAppVersionAvailable();
    });
  }

  private async displayNewAppVersionAvailable(): Promise<void> {
    const lastRememberTime = await this.systemService.getNewVersionAvailableLastRememberTime();
    const diffInHours = lastRememberTime ? moment().diff(moment(lastRememberTime), 'hours') : -1;
    if (diffInHours <= 4 && diffInHours !== -1) {
      return;
    }

    const message = this.translation.translate('MESSAGES.NEW_APP_VERSION_AVAILABLE');
    await this.messageHelper.showInfo(message, 3000, false);
    await this.systemService.updateNewVersionAvailableLastRememberTime();
  }

  private subscribeToPauseResumeEvents() {
    console.debug('Subscribed to app:pause event');
    this.platform.pause.subscribe(async () => {
      await this.appService.recordAppGoingToBackground();
    });

    console.debug('Subscribed to app:resume event');
    this.platform.resume.subscribe(async () => {
      try {
        await this.garbageCollectorService.runOnResumeIfNeeded();
      } catch (error) {
        console.warn('Garbage collector failed on resume', error);
      }
      await this.dataService.checkCurrentData();
      await this.syncNotificationsIgnoringFailures();
    });
  }

  // Best-effort refresh of the stored notifications and the header unread badge: offline, the stored
  // copy is what the user keeps seeing until the next chance to sync.
  private async syncNotificationsIgnoringFailures(): Promise<void> {
    try {
      await this.syncNotifications.execute();
    } catch (error) {
      console.warn('Could not sync notifications', error);
    }
  }

  private async dismissModalsAfterSessionExpiration(): Promise<void> {
    let modal = await this.modalCtrl.getTop();
    while (modal) {
      // An expired session must bypass forms' normal dismissal guards.
      modal.canDismiss = true;
      await modal.dismiss(undefined, 'session-expired');
      modal = await this.modalCtrl.getTop();
    }
  }

  private async doLogout(): Promise<void> {
    await this.logout.execute();
    // since logout can come from an out-of-Angular event, we need to wrap inside ngZone,
    // otherwise login page does not work properly
    await this.ngZone.run(() => this.router.navigateByUrl('/login', { replaceUrl: true }));
  }

  /**
   * The platform is ready and our plugins are available.
   * Here you can do any higher level native things you might need.
   */
  private async configureCordova(): Promise<void> {
    if (!this.platform.is('cordova')) {
      return;
    }

    await this.configureAndroid();

    // Fatal native crashes are reported to Firebase via the cordova-plugin-firebasex, since Sentry plugin did not work.
    // Other errors are reported with Sentry javascript using the error handler class, since it allows better debugging.
    await this.configureFirebase();
  }

  private async configureAndroid(): Promise<void> {
    if (!this.platform.is('android')) {
      return;
    }
    // No configuration needed atm
  }

  private async configureFirebase() {
    if (!this.platformService.isRealMobileDevice()) {
      return;
    }

    // Fatal native crashes are reported to Firebase via cordova-plugin-firebasex.
    await this.firebaseX.setCrashlyticsCollectionEnabled(true);
    await this.firebaseX.setAnalyticsCollectionEnabled(true);

    // Push notifications: bind FCM listeners and cache the token. Device registration in the
    // backend happens on login/logout (see addEventListeners).
    await this.pushNotificationService.init();
  }
}
